import React from 'react';
import useEffectOnce from 'react-use/lib/useEffectOnce';
import useApi from '../../_hooks/useApi';
import { urlWithParams } from '../../CommonUtils/urlWithParams';
import { TabHeader } from '../components/TabHeader/TabHeader';
import { TabHeaderLoading } from '../components/TabHeader/TabHeader.loading';
import { PlanningLabel } from '../components/PlanningLabel/PlanningLabel';
import { Box } from '../../CommonComponents/OPL/Box/Box';
import { MgpSlug } from '../MoneyGuideProTypes';
import { SummaryLoading } from '../Summary/Summary.loading';
import { InsuranceLoading } from '../Insurance/Insurance.loading';
import { GoalsLoading } from '../Goals/Goals.loading';
import { NetWorthLoading } from '../NetWorth/NetWorth.loading';
import { SummaryEmpty } from '../Summary/Summary.empty';
import { InsuranceEmpty } from '../Insurance/Insurance.empty';
import { GoalsEmpty } from '../Goals/Goals.empty';
import { NetWorthEmpty } from '../NetWorth/NetWorth.empty';
import { useRouteParams } from '../../_hooks/useRouteParams';

interface Props {
  tabTitle: string;
  mgpItemId: number;
  clientId: string;
  reducerPrefix: string;
  asOfDate: string | null;
  isEmpty: boolean;
  isPristine: boolean;
}

const LOADING_COMPONENT_DICT = {
  [MgpSlug.Summary]: SummaryLoading,
  [MgpSlug.Insurance]: InsuranceLoading,
  [MgpSlug.Goals]: GoalsLoading,
  [MgpSlug.Networth]: NetWorthLoading,
};

const EMPTY_COMPONENT_DICT = {
  [MgpSlug.Summary]: SummaryEmpty,
  [MgpSlug.Insurance]: InsuranceEmpty,
  [MgpSlug.Goals]: GoalsEmpty,
  [MgpSlug.Networth]: NetWorthEmpty,
};

export const Tab: React.FC<Props> = ({
  tabTitle,
  mgpItemId,
  clientId,
  reducerPrefix,
  asOfDate,
  isEmpty,
  isPristine,
  children,
}) => {
  const { tabSlug } = useRouteParams<{ tabSlug: string }>();

  const [requestReport, { loading: reportLoading, errors: reportErrors }] = useApi(
    urlWithParams('/Consumer/MgpReport/MgpReportItem', { clientId, mgpItemId }),
    {
      reduxActionPrefix: reducerPrefix,
    },
  );

  useEffectOnce(() => {
    if (isPristine) {
      requestReport();
    }
  });

  if (tabSlug === undefined || !(tabSlug in LOADING_COMPONENT_DICT)) {
    return null;
  }

  const LoadingComponent = LOADING_COMPONENT_DICT[tabSlug];
  const EmptyComponent = EMPTY_COMPONENT_DICT[tabSlug];

  if (reportLoading) {
    return (
      <>
        <TabHeaderLoading title={tabTitle} />
        <Box mx="8">
          <LoadingComponent />
        </Box>
      </>
    );
  }

  if (reportErrors.length > 0) {
    return (
      <>
        <TabHeader clientId={clientId} title={tabTitle} asOfDate={asOfDate} />
        <Box mx="8">
          <EmptyComponent />
        </Box>
        <PlanningLabel />
      </>
    );
  }

  if (isPristine) {
    return null;
  }

  if (isEmpty) {
    return (
      <>
        <TabHeader clientId={clientId} title={tabTitle} asOfDate={asOfDate} />
        <Box mx="8">
          <EmptyComponent />
        </Box>
        <PlanningLabel />
      </>
    );
  }

  return (
    <>
      <TabHeader clientId={clientId} title={tabTitle} asOfDate={asOfDate} />
      {children}
      <PlanningLabel />
    </>
  );
};
